import {
  addGroupAppInputSchema,
  createGroupInputSchema,
  createGroupVariableInputSchema,
  createGroupDimensionInputSchema,
  createGroupMeasureInputSchema,
  groupAppSchema,
  groupDetailResponseSchema,
  groupSchema,
  groupVariableSchema,
  groupDimensionSchema,
  groupMeasureSchema,
  listGroupAppsResponseSchema,
  listGroupsResponseSchema,
  listGroupVariablesResponseSchema,
  listGroupDimensionsResponseSchema,
  listGroupMeasuresResponseSchema,
  toStandardError,
  updateGroupInputSchema,
  updateGroupVariableInputSchema,
  updateGroupDimensionInputSchema,
  updateGroupMeasureInputSchema,
  jobSchema,
  checkGroupExpressionsResponseSchema,
  type AddGroupAppInput,
  type AppExpressionCheckResult,
  type CreateGroupInput,
  type CreateGroupVariableInput,
  type CreateGroupDimensionInput,
  type CreateGroupMeasureInput,
  type Group,
  type GroupApp,
  type GroupDetail,
  type GroupVariable,
  type GroupVariableWithSync,
  type GroupDimension,
  type GroupDimensionWithSync,
  type GroupMeasure,
  type GroupMeasureWithSync,
  type GroupWithCounts,
  type UpdateGroupInput,
  type UpdateGroupVariableInput,
  type UpdateGroupDimensionInput,
  type UpdateGroupMeasureInput,
  type Job,
  type ConflictResolution,
  bulkAddGroupAppsInputSchema,
  bulkAddGroupAppsResultSchema,
  type BulkAddGroupAppsInput,
  type BulkAddGroupAppsResult,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequest, apiRequestData, apiRequestJson } from "../api-client";

// =============================================================================
// Groups CRUD
// =============================================================================

export async function listGroups(params?: {
  limit?: number;
  cursor?: string;
}): Promise<{ data: GroupWithCounts[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) {
    search.set("limit", String(params.limit));
  }
  if (params?.cursor) {
    search.set("cursor", params.cursor);
  }

  const response = await apiRequestJson({
    path:
      search.size > 0 ? `${API_V1_PREFIX}/groups?${search.toString()}` : `${API_V1_PREFIX}/groups`,
    responseSchema: listGroupsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response;
}

export async function getGroup(id: string): Promise<GroupDetail> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${id}`,
    responseSchema: groupDetailResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function createGroup(input: CreateGroupInput): Promise<Group> {
  const validated = createGroupInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups`,
    method: "POST",
    body: validated.data,
    dataSchema: groupSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function updateGroup(id: string, patch: UpdateGroupInput): Promise<Group> {
  const validated = updateGroupInputSchema.safeParse(patch);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${id}`,
    method: "PATCH",
    body: validated.data,
    dataSchema: groupSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function deleteGroup(id: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/groups/${id}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

// =============================================================================
// Group Apps
// =============================================================================

export async function listGroupApps(groupId: string): Promise<GroupApp[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${groupId}/apps`,
    responseSchema: listGroupAppsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function addGroupApp(groupId: string, input: AddGroupAppInput): Promise<GroupApp> {
  const validated = addGroupAppInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/apps`,
    method: "POST",
    body: validated.data,
    dataSchema: groupAppSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function bulkAddGroupApps(
  groupId: string,
  input: BulkAddGroupAppsInput,
): Promise<BulkAddGroupAppsResult> {
  const validated = bulkAddGroupAppsInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/apps/bulk`,
    method: "POST",
    body: validated.data,
    dataSchema: bulkAddGroupAppsResultSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function removeGroupApp(groupId: string, groupAppId: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/groups/${groupId}/apps/${groupAppId}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

// =============================================================================
// Group Variables
// =============================================================================

export async function listGroupVariables(groupId: string): Promise<GroupVariableWithSync[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${groupId}/variables`,
    responseSchema: listGroupVariablesResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function getGroupVariable(
  groupId: string,
  variableId: string,
): Promise<GroupVariableWithSync> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${groupId}/variables/${variableId}`,
    responseSchema: listGroupVariablesResponseSchema.shape.data.element.transform((d) => ({
      data: d,
    })),
    badResponseMessage: "Unexpected response from server",
  });

  return response.data as unknown as GroupVariableWithSync;
}

export async function createGroupVariable(
  groupId: string,
  input: CreateGroupVariableInput,
): Promise<GroupVariable> {
  const validated = createGroupVariableInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/variables`,
    method: "POST",
    body: validated.data,
    dataSchema: groupVariableSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function updateGroupVariable(
  groupId: string,
  variableId: string,
  patch: UpdateGroupVariableInput,
): Promise<GroupVariable> {
  const validated = updateGroupVariableInputSchema.safeParse(patch);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/variables/${variableId}`,
    method: "PATCH",
    body: validated.data,
    dataSchema: groupVariableSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function deleteGroupVariable(groupId: string, variableId: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/groups/${groupId}/variables/${variableId}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

// =============================================================================
// Sync Operations
// =============================================================================

export async function auditGroup(groupId: string): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/audit`,
    method: "POST",
    body: {},
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export interface SyncGroupOptions {
  appIds?: string[];
  variableIds?: string[];
  dimensionIds?: string[];
  measureIds?: string[];
  conflictResolutions?: Record<string, ConflictResolution>;
}

export async function syncGroup(groupId: string, options?: SyncGroupOptions): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/sync`,
    method: "POST",
    body: options ?? {},
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function auditAllGroups(): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/audit-all`,
    method: "POST",
    body: {},
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function syncAllGroups(): Promise<Job> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/sync-all`,
    method: "POST",
    body: {},
    dataSchema: jobSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export type { AppExpressionCheckResult };

export type ValidateGroupExpressionsResult =
  | { valid: true; results: AppExpressionCheckResult[] }
  | { valid: false; results: AppExpressionCheckResult[] };

export async function validateGroupExpressions(
  groupId: string,
  options?: SyncGroupOptions,
): Promise<ValidateGroupExpressionsResult> {
  const { payload, status } = await apiRequest({
    path: `${API_V1_PREFIX}/groups/${encodeURIComponent(groupId)}/validate-expressions`,
    method: "POST",
    body: options ?? {},
  }).catch((err: unknown) => {
    if (
      err &&
      typeof err === "object" &&
      "code" in err &&
      (err as { code: string }).code === "EXPRESSION_VALIDATION_FAILED" &&
      "details" in err
    ) {
      const details = (err as { details: unknown }).details;
      if (details && typeof details === "object" && "results" in details) {
        const parsed = checkGroupExpressionsResponseSchema.safeParse({
          results: (details as { results: unknown }).results,
        });
        if (parsed.success) {
          return { payload: { data: parsed.data }, status: 400 };
        }
      }
    }
    throw err;
  });

  const data =
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    payload.data &&
    typeof payload.data === "object" &&
    "results" in payload.data
      ? payload.data
      : null;

  const parsed = checkGroupExpressionsResponseSchema.safeParse(data);
  const results = parsed.success ? parsed.data.results : [];

  return {
    valid: status === 200,
    results,
  };
}

// =============================================================================
// Group Dimensions
// =============================================================================

export async function listGroupDimensions(groupId: string): Promise<GroupDimensionWithSync[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${groupId}/dimensions`,
    responseSchema: listGroupDimensionsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function createGroupDimension(
  groupId: string,
  input: CreateGroupDimensionInput,
): Promise<GroupDimension> {
  const validated = createGroupDimensionInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/dimensions`,
    method: "POST",
    body: validated.data,
    dataSchema: groupDimensionSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function updateGroupDimension(
  groupId: string,
  dimensionId: string,
  patch: UpdateGroupDimensionInput,
): Promise<GroupDimension> {
  const validated = updateGroupDimensionInputSchema.safeParse(patch);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/dimensions/${dimensionId}`,
    method: "PATCH",
    body: validated.data,
    dataSchema: groupDimensionSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function deleteGroupDimension(groupId: string, dimensionId: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/groups/${groupId}/dimensions/${dimensionId}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

// =============================================================================
// Group Measures
// =============================================================================

export async function listGroupMeasures(groupId: string): Promise<GroupMeasureWithSync[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/groups/${groupId}/measures`,
    responseSchema: listGroupMeasuresResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function createGroupMeasure(
  groupId: string,
  input: CreateGroupMeasureInput,
): Promise<GroupMeasure> {
  const validated = createGroupMeasureInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/measures`,
    method: "POST",
    body: validated.data,
    dataSchema: groupMeasureSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function updateGroupMeasure(
  groupId: string,
  measureId: string,
  patch: UpdateGroupMeasureInput,
): Promise<GroupMeasure> {
  const validated = updateGroupMeasureInputSchema.safeParse(patch);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/groups/${groupId}/measures/${measureId}`,
    method: "PATCH",
    body: validated.data,
    dataSchema: groupMeasureSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function deleteGroupMeasure(groupId: string, measureId: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/groups/${groupId}/measures/${measureId}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}
